import { useMemo } from 'react'

import { IWithMoney, Price, Product } from '@Generated/graphql'
import { getBaseAttribute, getPersonalizationsFromProduct, getPersonalizationTypes } from '@Globals/CMS/ProductHelpers'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'
import { Money } from '@Globals/Money'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

export interface ChipOptions {
  buyXPayForY?: { x: number, y: number }
  clubValue?: string
  enableDiscount: boolean
  freeDelivery: boolean
  isBestSeller?: boolean
  isNew: boolean
  isPopularGift?: boolean
  personalizationTypes?: string[]
  price?: Price
  showChips: boolean
}

export const useChipAssets = (product?: Product): ChipOptions => {
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()
  const enableBuy3 = useSplitTest('buy_three_pay_for_two_v1', 'b')

  return useMemo(() => {
    const { value: freeDeliveryThreshold } = freeShippingThreshold as Money || 0
    const isBestSeller = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isBestSeller') || false
    const clubValue = product?.masterVariant?.price?.discount?.clubValue?.format || product?.masterVariant?.price?.clubValue?.format || undefined
    const isBundleOffer = product?.type === 'bundles'
    const isNew = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isNew') || false
    const isPopularGift = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isPopularGift') || false
    const isGiftcard = `${product?.id}` === '26342'
    const price = product?.masterVariant?.price?.discount || product?.masterVariant?.price || ({} as IWithMoney)
    const freeDelivery = !!(freeDeliveryThreshold && price?.value && (freeDeliveryThreshold < price.value))
        || cheapestShippingPrice === null
        || cheapestShippingPrice?.value === 0

    // TODO: Enable this after BW23 th-5438
    const bracletMassDiscount = enableBuy3 // && product?.primaryCategories?.some(a => a.id === '7')
    // const sunglassMassDiscount = product?.primaryCategories?.some(a => a.id === '9')
    const buy3payFor2 = bracletMassDiscount ? { x: 3, y: 2 } : undefined

    const showChips = !!(isNew || (
      (product?.masterVariant?.price?.discount?.percent) || product?.masterVariant?.price?.discount?.code?.context !== '')
        || freeDelivery || isBundleOffer)

    return {
      buyXPayForY: buy3payFor2,
      isBestSeller,
      isBundleOffer,
      clubValue,
      isNew,
      price: product?.masterVariant?.price ?? undefined,
      showChips,
      freeDelivery,
      isPopularGift,
      productType: product?.type || '',
      enableDiscount: !isGiftcard,
      personalizationTypes: product ? getPersonalizationTypes(getPersonalizationsFromProduct(product)) : [],
    }
  }, [freeShippingThreshold, product, cheapestShippingPrice, enableBuy3])
}
